import React from "react";
import { FlucsDownIcon, FlucsUpIcon, Odds } from "@picklebet/ui";
import { OddsWithChanges } from "@/features/betslip/components/parts/OddsWithChanges";
import { useOddsFormat } from "@/hooks/firestore/user/useOddsFormat";

import * as styles from "@/features/betslip/components/parts/OddsDisplay.module.scss";

type OddsDisplayProps = {
  /** Current odds on the selection. */
  odds: number;
  /** Incoming change awaiting acceptance (manual, or accept-higher that
   *  dropped). For a multi this is the product of the pending leg prices.
   *  While it differs from `odds` the up/down indicator is held until the
   *  change resolves rather than easing out on a timer. */
  newOdds?: number;
  /** Show "SP" — starting-price single. */
  sp?: boolean;
  /** Show a dash instead of a price — a multi carrying an SP leg, or a live
   *  selection this user can't bet. */
  dash?: boolean;
  /** In a stake-strip odds cell, show the old price struck through to the left
   *  of the incoming price with a direction arrow between. Inline leg odds
   *  leave this off and keep the single held-indicator display. */
  showPreviousOdds?: boolean;
};

export const OddsDisplay = ({
  odds,
  newOdds,
  sp,
  dash,
  showPreviousOdds,
}: OddsDisplayProps) => {
  if (dash) return <span data-testid="odds-display">-</span>;
  if (sp) return <span data-testid="odds-display">SP</span>;

  if (showPreviousOdds && newOdds !== undefined && newOdds !== odds) {
    return <PreviousToNewOdds previous={odds} next={newOdds} />;
  }

  const hasIncoming = newOdds !== undefined && newOdds !== odds;
  return (
    <span className={styles.oddsCenter}>
      <OddsWithChanges
        odds={newOdds ?? odds}
        initialOdds={hasIncoming ? odds : undefined}
        animationLength={hasIncoming ? null : undefined}
      />
    </span>
  );
};

type PreviousToNewOddsProps = {
  previous: number;
  next: number;
};

const PreviousToNewOdds = ({ previous, next }: PreviousToNewOddsProps) => {
  const format = useOddsFormat();
  const direction = next < previous ? "down" : "up";

  return (
    <span
      className={styles.oddsChange}
      data-status={direction}
      data-testid="odds-display"
    >
      <Odds
        base={previous}
        format={format}
        zero="1.00"
        className={styles.previousOdds}
      />
      <span className={styles.incoming}>
        {direction === "down" ? (
          <FlucsDownIcon className={styles.arrowDown} />
        ) : (
          <FlucsUpIcon className={styles.arrowUp} />
        )}
        <Odds
          base={next}
          format={format}
          zero="1.00"
          className={styles.newOdds}
        />
      </span>
    </span>
  );
};
